import React from "react";
import {
  Box,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Checkbox,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {renderBracketedText} from "../../context/videoTextColor";

interface Selection {
  cardTitle: string;
  sides: {
    left: boolean;
    right: boolean;
  };
}

interface ModalPopupStudioProps {
  previewImage?: string | null | undefined | any;
  videoShot: boolean;
  setVideoShot: (value: boolean) => void;
  selected?: Selection[];
  setSelected?: React.Dispatch<React.SetStateAction<Selection[]>>;
  selectedCard?: {
    title: string;
    subTitle?: string;
    image: string;
    videoLeft: string;
    videoRight: string;
  };
  setPrompt?: (value: string) => void;
  prompt?: string | any;
  uploadfile?: any;
  type?: "camera" | "video";
  videoData?: {
    videoImage: string;
    modalPrompt: string;
  };
}

export default function ModalPopupStudio({
  previewImage,
  videoShot,
  setVideoShot,
  selected,
  setSelected,
  selectedCard,
  setPrompt,
  prompt,
  uploadfile,
  type,
  videoData,
}: ModalPopupStudioProps) {
  const getTotalSelections = () => {
    return (
      selected?.reduce(
        (total, item) =>
          total + (item.sides.left ? 1 : 0) + (item.sides.right ? 1 : 0),
        0
      ) || 0
    );
  };


  const toggleSelection = (side: "left" | "right") => {
    if (!selected || !setSelected || !selectedCard) return;

    setSelected((prev: Selection[]) => {
      const totalSelections = getTotalSelections();
      const index = prev.findIndex(
        (item) => item.cardTitle === selectedCard.title
      );
      const current =
        index >= 0
          ? prev[index]
          : {
            cardTitle: selectedCard.title,
            sides: { left: false, right: false },
          };

      // Max 3 limit
      if (!current.sides[side] && totalSelections >= 3) {
        return prev;
      }
      const updatedSides = {
        ...current.sides,
        [side]: !current.sides[side],
      };

      const updated = { ...current, sides: updatedSides };

      let newSelections = [...prev];
      if (index >= 0) {
        newSelections[index] = updated;
      } else {
        newSelections.push(updated);
      }
      newSelections = newSelections.filter(
        (sel) => sel.sides.left || sel.sides.right
      );

      if (setPrompt) {
        const cameraControls: string[] = [];

        newSelections.forEach((sel) => {
          const card = selectedCard; // Assuming selectedCard contains camera details
          if (sel.sides.left && card?.title) {
            cameraControls.push(card.title);
          }
          if (sel.sides.right && card?.subTitle) {
            cameraControls.push(card.subTitle);
          }
        });

        const controlText =
          cameraControls.length > 0 ? `[${cameraControls.join(", ")}]` : "";
        const captionText = uploadfile?.caption || "";

        const fullPrompt = `${controlText} ${captionText}`.trim();
        setPrompt(fullPrompt);
      }

      return newSelections;
    });
  };

  const currentSelection = selected?.find(
    (sel) => sel.cardTitle === selectedCard?.title
  );
  const maxReached = getTotalSelections() >= 3;

  return (
    <Box>
      <Dialog
        open={videoShot}
        onClose={() => setVideoShot(false)}
        maxWidth={selectedCard?.subTitle || type === "video" ? "md" : "sm"}
        // fullWidth
        className={type === 'camera' ? "modalMainContainer" : "modalMainContainerImage"}
      >
        <DialogTitle className="modalTitle">
          <Box className = "modalTitleBox">
          <Typography className="modalTitleText">
          <img src={'/assets/Cameracontrol.svg'} alt="Camera Control" className="cameracontrolIcon" />
          {type === "camera" ? "Camera Controls - Preview" : "Preview"}
          </Typography>
           <IconButton
            sx={{ color: "#fff" }}
            onClick={() => setVideoShot(false)}
          >
            <CloseIcon />
          </IconButton>
          </Box>
        </DialogTitle>
        <Divider className="modalDivider" />

        <DialogContent className="modelContent">
          {type === "camera" && selectedCard && (
            <Grid
              container
              spacing={2}
              padding={3}
              className="modalGridContainer"
            >
              <Grid item xs={12} sm={selectedCard.subTitle ? 6 : 12}>
                <Typography variant="body1" className="selectedCardTitle">
                  {selectedCard.title}
                </Typography>
                <div className="videoContainer">
                  <video
                    src={selectedCard.videoLeft}
                    className="videoImage"
                    autoPlay
                  />
                  <Checkbox
                    checked={currentSelection?.sides.left || false}
                    disabled={!currentSelection?.sides.left && maxReached}
                    onChange={() => toggleSelection("left")}
                    className="checkBoxImage"
                  />
                </div>
              </Grid>
              {selectedCard.subTitle && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" className="selectedCardTitle">
                    {selectedCard.subTitle}
                  </Typography>
                  <div className="videoContainer">
                    <video
                      src={selectedCard.videoRight}
                      className="videoImage"
                      autoPlay
                    />
                    <Checkbox
                      checked={currentSelection?.sides.right || false}
                      disabled={!currentSelection?.sides.right && maxReached}
                      onChange={() => toggleSelection("right")}
                      className="checkBoxImage"
                    />
                  </div>
                </Grid>
              )}
            </Grid>
          )}

          {type === "video" && videoData ? (
            <Box>
              <Box className="videoDataImageContainer">
              <img src={videoData.videoImage} alt="Video" className="videoDataImage" />
              </Box>
              <Box className="videoPromptContainer">
              <Typography variant="body2" className="videoPromptText">
                {renderBracketedText(videoData.modalPrompt)}
              </Typography>
              </Box>
            </Box>
          ) : (
           previewImage && (
    <Box>
      <Box
        className="imagePromptContainer videoDataImageContainer"
        sx={{
          display: Array.isArray(previewImage) ? "flex" : "block",
          gap: Array.isArray(previewImage) ? 2 : 0,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {Array.isArray(previewImage) ? (
          previewImage.map((img: any, idx: number) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mx: 1,
              }}
            >
              <img
                src={img?.inputImage || img}
                alt={`Input Preview ${idx + 1}`}
                className="videoDataImage"
                style={{ width: "100%", maxWidth: 220, marginBottom: 8 }}
              />
              {img?.cartoonImage && (
                <img
                  src={img.cartoonImage}
                  alt={`Cartoon Preview ${idx + 1}`}
                  className="videoDataImage"
                  style={{ width: "100%", maxWidth: 220 }}
                />
              )}
            </Box>
          ))
        ) : (
          <Box className="previewImageBox">
            <img
              src={previewImage?.inputImage || previewImage}
              alt="Preview"
              className="videoDataImage"
              style={{ width: "100%", maxWidth: 220, marginBottom: 8 }}
            />
            {previewImage?.cartoonImage && (
              <img
                src={previewImage.cartoonImage}
                alt="Cartoon Preview"
                className="videoDataImage"
                style={{ width: "100%", maxWidth: 220 }}
              />
            )}
          </Box>
        )}
      </Box>
      <Box className="videoPromptContainer">
        <Typography variant="body2" className="videoPromptText">
          {prompt}
        </Typography>
      </Box>
    </Box>
  )
          )}
          
        </DialogContent>
        {type === "camera" && selectedCard &&
          <DialogActions className="modalActions">
            <Button
              variant="contained"
              sx={{ bgcolor: "#6A1B9A", width: "100px" }}
              className="modalButton"
              onClick={() => setVideoShot(false)}
            >
              Apply
            </Button>
          </DialogActions>
        }
      </Dialog>
    </Box>
  );
}